import { z } from "zod";
import { dateOnlySchema, timestampSchema, uuidSchema } from "./api.ts";
import { gstSupplyTypeSchema } from "./invoices.ts";
import { currencyCodeSchema, exponentOf, minorSchema, type CurrencyCode } from "./money.ts";

/**
 * MULTI-CURRENCY AND FX (roadmap item 17, PRD §9 Item 17, integrations-reference §9).
 *
 * Vocabulary, used everywhere in this file and in the `fx` module:
 *
 *  · A RATE is a decimal string with at most 10 fractional digits (NUMERIC(20,10)
 *    in the database) that reads "1 BASE = rate QUOTE". `convertMinor(amount,
 *    base, quote, rate)` in money.ts is the only place it is applied to money.
 *  · A document's rate is SNAPSHOTTED when it is issued and never recomputed.
 *  · REALISED gain/loss is posted when foreign money is received at a rate that
 *    differs from the document's; UNREALISED gain/loss is the period-end
 *    revaluation of what is still open (Ind AS 21 / AS 11 — confirm with a CA).
 *
 * Nothing here uses float arithmetic on money or rates (PRD I-7).
 */

// ---------------------------------------------------------------------------
// Vocabulary
// ---------------------------------------------------------------------------

/** Where a reference rate came from. `manual` = a platform-seeded rate, not a workspace override. */
export const FX_RATE_SOURCES = ["frankfurter", "ecb", "openexchangerates", "manual"] as const;
export type FxRateSource = (typeof FX_RATE_SOURCES)[number];
export const fxRateSourceSchema = z.enum(FX_RATE_SOURCES);

/** A resolved rate's provenance: a feed row, this workspace's override, or no conversion at all. */
export const FX_RESOLVED_SOURCES = [...FX_RATE_SOURCES, "override", "identity"] as const;
export type FxResolvedSource = (typeof FX_RESOLVED_SOURCES)[number];
export const fxResolvedSourceSchema = z.enum(FX_RESOLVED_SOURCES);

/**
 * How a rate was obtained:
 *  identity — same currency, rate 1 · feed — the daily reference feed ·
 *  override — this workspace's audited manual rate · document — a rate typed on
 *  the draft itself · realised — the base-currency amount the bank actually credited.
 */
export const FX_RATE_KINDS = ["identity", "feed", "override", "document", "realised"] as const;
export type FxRateKind = (typeof FX_RATE_KINDS)[number];
export const fxRateKindSchema = z.enum(FX_RATE_KINDS);

/** direct = a stored pair · inverse = 1 ÷ the stored opposite pair · cross = two pairs through a pivot (EUR). */
export const FX_DERIVATIONS = ["identity", "direct", "inverse", "cross"] as const;
export type FxDerivation = (typeof FX_DERIVATIONS)[number];
export const fxDerivationSchema = z.enum(FX_DERIVATIONS);

/** A lookup falls back to earlier dates (weekends, bank holidays) at most this far. */
export const FX_MAX_LOOKBACK_DAYS = 10;
/** A rate older than this (in days) is flagged stale — the 48-hour alert of integrations-reference §9.3. */
export const FX_STALE_AFTER_DAYS = 2;
/** Export realisation: IGST + interest falls due if foreign exchange is not realised within a year (§1.8). */
export const FX_EXPORT_REALISATION_DAYS = 365;
/** … and the tracker raises its alert this many days after the invoice date. */
export const FX_EXPORT_ALERT_DAYS = 300;

// ---------------------------------------------------------------------------
// Rate arithmetic — BigInt fixed point, 10 fractional digits
// ---------------------------------------------------------------------------

export const FX_RATE_SCALE = 10;
const SCALE = 10n ** BigInt(FX_RATE_SCALE);
const RATE_PATTERN = /^\d{1,10}(\.\d{1,10})?$/;

/** True for a positive decimal with ≤ 10 integer and ≤ 10 fractional digits. */
export function isValidRate(value: string): boolean {
  return RATE_PATTERN.test(value) && /[1-9]/.test(value);
}

/** "83.1200000000" → 831200000000n (the rate × 10^10). Throws on anything that is not a rate. */
export function rateToScaled(rate: string): bigint {
  const trimmed = rate.trim();
  if (!/^\d+(\.\d+)?$/.test(trimmed)) throw new Error(`Invalid exchange rate: ${rate}`);
  const [whole = "0", fraction = ""] = trimmed.split(".");
  return BigInt(whole + (fraction + "0".repeat(FX_RATE_SCALE)).slice(0, FX_RATE_SCALE));
}

/** 831200000000n → "83.12" (trailing zeros trimmed, never exponent notation). */
export function scaledToRate(scaled: bigint): string {
  if (scaled <= 0n) throw new Error("An exchange rate must be positive.");
  const whole = scaled / SCALE;
  const fraction = (scaled % SCALE).toString().padStart(FX_RATE_SCALE, "0").replace(/0+$/, "");
  return fraction ? `${whole}.${fraction}` : `${whole}`;
}

/** Canonical text of a rate: "83.1200000000" and "83.12" both become "83.12". */
export function normalizeRate(rate: string): string {
  return scaledToRate(rateToScaled(rate));
}

function divRound(numerator: bigint, denominator: bigint): bigint {
  const q = numerator / denominator;
  const r = numerator % denominator;
  return r * 2n >= denominator ? q + 1n : q;
}

/** 1 ÷ rate, rounded half-up at the 10th decimal. */
export function invertRate(rate: string): string {
  return scaledToRate(divRound(SCALE * SCALE, rateToScaled(rate)));
}

/**
 * A cross rate through a pivot: given 1 P = `pivotToFrom` FROM and
 * 1 P = `pivotToTo` TO, returns 1 FROM = x TO. This is how an EUR-based feed
 * (ECB, Frankfurter) answers USD → INR.
 */
export function crossRate(pivotToFrom: string, pivotToTo: string): string {
  return scaledToRate(divRound(rateToScaled(pivotToTo) * SCALE, rateToScaled(pivotToFrom)));
}

/** Chains two rates: 1 A = r1 B and 1 B = r2 C → 1 A = r1 × r2 C. */
export function chainRates(first: string, second: string): string {
  return scaledToRate(divRound(rateToScaled(first) * rateToScaled(second), SCALE));
}

/**
 * The rate implied by two amounts in minor units: `quoteMinor` of QUOTE was
 * paid for `baseMinor` of BASE. Exponents are respected (JPY 0, KWD 3).
 */
export function rateFromAmounts(baseMinor: number, base: CurrencyCode, quoteMinor: number, quote: CurrencyCode): string {
  if (baseMinor <= 0 || quoteMinor <= 0) throw new Error("Both amounts must be positive to imply a rate.");
  const numerator = BigInt(quoteMinor) * 10n ** BigInt(exponentOf(base)) * SCALE;
  const denominator = BigInt(baseMinor) * 10n ** BigInt(exponentOf(quote));
  return scaledToRate(divRound(numerator, denominator));
}

/** Compares two rates numerically (-1, 0, 1). */
export function compareRates(a: string, b: string): number {
  const x = rateToScaled(a);
  const y = rateToScaled(b);
  return x === y ? 0 : x < y ? -1 : 1;
}

/**
 * `part × total ÷ whole`, rounded half away from zero, exact in BigInt. Used to
 * pro-rate a realised base amount across the invoices one receipt settled.
 */
export function prorateMinor(totalMinor: number, partMinor: number, wholeMinor: number): number {
  if (wholeMinor === 0) return 0;
  const neg = (totalMinor < 0) !== (partMinor < 0);
  const num = BigInt(Math.abs(totalMinor)) * BigInt(Math.abs(partMinor));
  const out = divRound(num, BigInt(Math.abs(wholeMinor)));
  return Number(neg ? -out : out);
}

/**
 * Splits `totalMinor` across `weights` by the largest-remainder method, EXACTLY
 * (BigInt, no float): the shares ALWAYS add up to the total, and a tie goes to
 * the earlier weight, so every caller that splits the same total the same way
 * gets the same shares. Rounding each share on its own (`prorateMinor` per
 * part) can leave the parts a paisa or two away from the whole.
 */
export function allocateExact(totalMinor: number, weights: readonly number[]): number[] {
  if (weights.some((w) => !Number.isSafeInteger(w) || w < 0)) throw new Error("allocateExact: weights must be non-negative integers");
  const sum = weights.reduce((a, b) => a + b, 0);
  if (sum === 0) return weights.map(() => 0);
  const negative = totalMinor < 0;
  const total = BigInt(Math.abs(totalMinor));
  const whole = BigInt(sum);
  const shares = weights.map((w) => (total * BigInt(w)) / whole);
  const remainders = weights.map((w, i) => total * BigInt(w) - (shares[i] as bigint) * whole);
  let left = total - shares.reduce((a, b) => a + b, 0n);
  const order = weights.map((_, i) => i).sort((a, b) => {
    const ra = remainders[a] as bigint;
    const rb = remainders[b] as bigint;
    return rb > ra ? 1 : rb < ra ? -1 : a - b;
  });
  for (const i of order) {
    if (left <= 0n) break;
    shares[i] = (shares[i] as bigint) + 1n;
    left -= 1n;
  }
  return shares.map((share) => Number(negative ? -share : share));
}

// ---------------------------------------------------------------------------
// Rates
// ---------------------------------------------------------------------------

/** INPUT: a positive decimal rate, "1 base = rate quote". */
export const fxRateValueSchema = z
  .string()
  .trim()
  .refine(isValidRate, "Use a positive decimal with at most 10 digits before and 10 after the point.");

/** One resolved rate — what every lookup, preview and snapshot is made of. */
export const fxRateSchema = z.object({
  base: currencyCodeSchema,
  quote: currencyCodeSchema,
  /** 1 base = rate quote. */
  rate: z.string(),
  /** The date that was asked for. */
  requestedDate: dateOnlySchema,
  /** The date the rate is actually from — earlier on weekends, holidays or a feed outage. */
  rateDate: dateOnlySchema,
  source: fxResolvedSourceSchema,
  kind: fxRateKindSchema,
  derivation: fxDerivationSchema,
  /** The workspace override that supplied it, when `kind` is `override`. */
  overrideId: uuidSchema.nullable(),
  ageDays: z.number().int(),
  /** Older than FX_STALE_AFTER_DAYS — shown with a warning, labelled "approx." in reports. */
  stale: z.boolean(),
  fetchedAt: timestampSchema.nullable(),
});
export type FxRateDto = z.infer<typeof fxRateSchema>;

/** GET /api/v1/fx/rates?base&quote&date (auth) → FxRate. No rate within FX_MAX_LOOKBACK_DAYS → NOT_FOUND. */
export const fxRateQuerySchema = z.object({
  base: currencyCodeSchema,
  quote: currencyCodeSchema,
  /** Defaults to today in the workspace timezone. */
  date: dateOnlySchema.optional(),
});
export type FxRateQuery = z.infer<typeof fxRateQuerySchema>;

/** GET /api/v1/fx/rates/history?base&quote&from&to (auth) → one row per day that has a rate. At most 366 days. */
export const fxRateHistoryQuerySchema = z
  .object({
    base: currencyCodeSchema,
    quote: currencyCodeSchema,
    from: dateOnlySchema.optional(),
    to: dateOnlySchema.optional(),
    source: fxRateSourceSchema.optional(),
  })
  .refine((q) => !q.from || !q.to || q.from <= q.to, { message: "`from` must be on or before `to`.", path: ["from"] });
export type FxRateHistoryQuery = z.infer<typeof fxRateHistoryQuerySchema>;

export const fxRateHistoryItemSchema = z.object({
  rateDate: dateOnlySchema,
  /** The rate that applies on this date: the override when there is one, else the feed. */
  rate: z.string(),
  feedRate: z.string().nullable(),
  feedSource: fxRateSourceSchema.nullable(),
  derivation: fxDerivationSchema.nullable(),
  overrideRate: z.string().nullable(),
  overrideId: uuidSchema.nullable(),
});
export type FxRateHistoryItemDto = z.infer<typeof fxRateHistoryItemSchema>;

export const fxRateHistorySchema = z.object({
  base: currencyCodeSchema,
  quote: currencyCodeSchema,
  from: dateOnlySchema,
  to: dateOnlySchema,
  items: z.array(fxRateHistoryItemSchema),
});
export type FxRateHistoryDto = z.infer<typeof fxRateHistorySchema>;

// ---------------------------------------------------------------------------
// Overrides (US-17.3: "Finance can override manually with a reason, and the override is audited")
// ---------------------------------------------------------------------------

export const fxOverrideSchema = z.object({
  id: uuidSchema,
  base: currencyCodeSchema,
  quote: currencyCodeSchema,
  rate: z.string(),
  rateDate: dateOnlySchema,
  reason: z.string(),
  createdByUserId: uuidSchema.nullable(),
  createdByName: z.string(),
  createdAt: timestampSchema,
  updatedAt: timestampSchema,
});
export type FxOverrideDto = z.infer<typeof fxOverrideSchema>;

/**
 * POST /api/v1/fx/overrides (perm `invoices:write` + `financials:view`) → 201 FxOverride.
 * One live override per currency pair (either direction) per date → CONFLICT.
 */
export const createFxOverrideInputSchema = z
  .object({
    base: currencyCodeSchema,
    quote: currencyCodeSchema,
    rate: fxRateValueSchema,
    /** Defaults to today in the workspace timezone. */
    rateDate: dateOnlySchema.optional(),
    reason: z.string().trim().min(3, "Say why the feed rate is being overridden.").max(500),
  })
  .strict()
  .refine((v) => v.base !== v.quote, { message: "An override needs two different currencies.", path: ["quote"] });
export type CreateFxOverrideInput = z.input<typeof createFxOverrideInputSchema>;

/** GET /api/v1/fx/overrides (perm `financials:view`) → { items }. Newest date first. */
export const listFxOverridesQuerySchema = z.object({
  base: currencyCodeSchema.optional(),
  quote: currencyCodeSchema.optional(),
  from: dateOnlySchema.optional(),
  to: dateOnlySchema.optional(),
});
export type ListFxOverridesQuery = z.infer<typeof listFxOverridesQuerySchema>;

// ---------------------------------------------------------------------------
// Refresh + conversion preview
// ---------------------------------------------------------------------------

/** POST /api/v1/fx/refresh (perm `org:manage`) → 202. Queues the feed fetch; never calls the network in the request. */
export const fxRefreshInputSchema = z
  .object({
    /** A historical date to backfill. Omit for the latest rates. */
    date: dateOnlySchema.optional(),
  })
  .strict();
export type FxRefreshInput = z.input<typeof fxRefreshInputSchema>;

export const fxRefreshSchema = z.object({
  jobId: uuidSchema.nullable(),
  queued: z.boolean(),
  date: dateOnlySchema.nullable(),
});
export type FxRefreshDto = z.infer<typeof fxRefreshSchema>;

/** GET /api/v1/fx/convert?amountMinor&from&to&date (perm `financials:view`) → a preview, never stored. */
export const fxConvertQuerySchema = z.object({
  amountMinor: z.coerce.number().int().safe(),
  from: currencyCodeSchema,
  to: currencyCodeSchema,
  date: dateOnlySchema.optional(),
});
export type FxConvertQuery = z.infer<typeof fxConvertQuerySchema>;

export const fxConversionSchema = z.object({
  amountMinor: minorSchema,
  from: currencyCodeSchema,
  to: currencyCodeSchema,
  convertedMinor: minorSchema,
  rate: fxRateSchema,
  /** True when the rate is stale — show it as "approx.". */
  approx: z.boolean(),
});
export type FxConversionDto = z.infer<typeof fxConversionSchema>;

// ---------------------------------------------------------------------------
// Document snapshots (US-17.3: "each invoice stores the rate, source and date at the moment it is issued")
// ---------------------------------------------------------------------------

export const fxDocumentRateSchema = z.object({
  documentId: uuidSchema,
  docType: z.string(),
  clientId: uuidSchema,
  documentDate: dateOnlySchema,
  currency: currencyCodeSchema,
  baseCurrency: currencyCodeSchema,
  rate: z.string(),
  rateKind: fxRateKindSchema,
  rateSource: fxResolvedSourceSchema,
  rateDate: dateOnlySchema,
  totalMinor: minorSchema,
  taxableMinor: minorSchema,
  totalBaseMinor: minorSchema,
  taxableBaseMinor: minorSchema,
  supplyType: gstSupplyTypeSchema,
  exportUnderLut: z.boolean(),
  /** "INR equivalent @ 1 USD = INR 83.12" — the Rule 34 line, or null for a base-currency document. */
  equivalentText: z.string().nullable(),
  snapshottedAt: timestampSchema,
});
export type FxDocumentRateDto = z.infer<typeof fxDocumentRateSchema>;

// ---------------------------------------------------------------------------
// Foreign receipts: realised INR, FIRA / e-BRC (US-17.5)
// ---------------------------------------------------------------------------

/** RBI purpose code, e.g. P1007 (advertising) or P1006 (consultancy / PR). */
export const purposeCodeSchema = z
  .string()
  .trim()
  .toUpperCase()
  .regex(/^P\d{4}$/, "Use an RBI purpose code such as P1007.");

export const fxReceiptSchema = z.object({
  paymentId: uuidSchema,
  /** False until somebody records the realisation; the realised gain then uses the feed rate. */
  recorded: z.boolean(),
  currency: currencyCodeSchema,
  baseCurrency: currencyCodeSchema,
  amountMinor: minorSchema,
  paymentDate: dateOnlySchema,
  /** Base-currency money the bank actually credited for this receipt. */
  realisedBaseMinor: minorSchema.nullable(),
  /** realisedBaseMinor ÷ amountMinor, as a rate. */
  effectiveRate: z.string().nullable(),
  bankChargesBaseMinor: minorSchema,
  firaNumber: z.string().nullable(),
  firaDate: dateOnlySchema.nullable(),
  ebrcNumber: z.string().nullable(),
  ebrcDate: dateOnlySchema.nullable(),
  purposeCode: z.string().nullable(),
  adBank: z.string().nullable(),
  notes: z.string().nullable(),
  updatedAt: timestampSchema.nullable(),
});
export type FxReceiptDto = z.infer<typeof fxReceiptSchema>;

/**
 * PUT /api/v1/fx/receipts/:paymentId (perm `payments:record`) → FxReceipt. An
 * upsert: omitted keys keep their current value, `null` clears. Recomputes the
 * realised gain of every invoice the receipt settled.
 */
export const upsertFxReceiptInputSchema = z
  .object({
    realisedBaseMinor: minorSchema.positive().nullable(),
    bankChargesBaseMinor: minorSchema.min(0),
    firaNumber: z.string().trim().min(1).max(64).nullable(),
    firaDate: dateOnlySchema.nullable(),
    ebrcNumber: z.string().trim().min(1).max(64).nullable(),
    ebrcDate: dateOnlySchema.nullable(),
    purposeCode: purposeCodeSchema.nullable(),
    adBank: z.string().trim().min(1).max(120).nullable(),
    notes: z.string().trim().max(2000).nullable(),
  })
  .partial()
  .strict();
export type UpsertFxReceiptInput = z.input<typeof upsertFxReceiptInputSchema>;
export type UpsertFxReceiptData = z.output<typeof upsertFxReceiptInputSchema>;

// ---------------------------------------------------------------------------
// Realised gain / loss
// ---------------------------------------------------------------------------

export const fxRealisedRowSchema = z.object({
  invoiceId: uuidSchema,
  invoiceNumber: z.string().nullable(),
  paymentId: uuidSchema,
  clientId: uuidSchema,
  currency: currencyCodeSchema,
  paymentDate: dateOnlySchema,
  /** Foreign money that settled the invoice (cash + TDS). */
  settledMinor: minorSchema,
  invoiceRate: z.string(),
  receiptRate: z.string(),
  receiptRateKind: fxRateKindSchema,
  receiptRateDate: dateOnlySchema.nullable(),
  /** settledMinor at the invoice's snapshot rate — what the books carried. */
  bookedBaseMinor: minorSchema,
  /** settledMinor at the receipt rate — what actually arrived. */
  realisedBaseMinor: minorSchema,
  /** realised − booked. Positive is a gain. */
  gainBaseMinor: minorSchema,
});
export type FxRealisedRowDto = z.infer<typeof fxRealisedRowSchema>;

export const fxCurrencyGainSchema = z.object({
  currency: currencyCodeSchema,
  settledMinor: minorSchema,
  bookedBaseMinor: minorSchema,
  realisedBaseMinor: minorSchema,
  gainBaseMinor: minorSchema,
});

/** GET /api/v1/fx/reports/realised?from&to (perms `invoices:read` + `financials:view`). */
export const fxRealisedQuerySchema = z
  .object({
    from: dateOnlySchema,
    to: dateOnlySchema,
    clientId: uuidSchema.optional(),
    currency: currencyCodeSchema.optional(),
  })
  .refine((q) => q.from <= q.to, { message: "`from` must be on or before `to`.", path: ["from"] });
export type FxRealisedQuery = z.infer<typeof fxRealisedQuerySchema>;

export const fxRealisedReportSchema = z.object({
  baseCurrency: currencyCodeSchema,
  from: dateOnlySchema,
  to: dateOnlySchema,
  gainBaseMinor: minorSchema,
  lossBaseMinor: minorSchema,
  netBaseMinor: minorSchema,
  byCurrency: z.array(fxCurrencyGainSchema),
  rows: z.array(fxRealisedRowSchema),
});
export type FxRealisedReportDto = z.infer<typeof fxRealisedReportSchema>;

// ---------------------------------------------------------------------------
// Unrealised revaluation (period end)
// ---------------------------------------------------------------------------

/** "2026-09" — a calendar month; its last day is the revaluation date. */
export const fxPeriodSchema = z.string().regex(/^\d{4}-(0[1-9]|1[0-2])$/, "Use YYYY-MM.");

export const fxRevaluationLineSchema = z.object({
  invoiceId: uuidSchema,
  invoiceNumber: z.string().nullable(),
  clientId: uuidSchema,
  currency: currencyCodeSchema,
  invoiceDate: dateOnlySchema,
  /** Foreign money still owed at the revaluation date. */
  openMinor: minorSchema,
  bookedRate: z.string(),
  /** False when the document predates FX snapshots and its booked rate had to be looked up. */
  bookedRateSnapshotted: z.boolean(),
  closingRate: z.string(),
  closingRateDate: dateOnlySchema,
  closingRateSource: fxResolvedSourceSchema,
  carryingBaseMinor: minorSchema,
  revaluedBaseMinor: minorSchema,
  unrealisedBaseMinor: minorSchema,
});
export type FxRevaluationLineDto = z.infer<typeof fxRevaluationLineSchema>;

export const fxRevaluationCurrencySchema = z.object({
  currency: currencyCodeSchema,
  openMinor: minorSchema,
  closingRate: z.string(),
  carryingBaseMinor: minorSchema,
  revaluedBaseMinor: minorSchema,
  unrealisedBaseMinor: minorSchema,
});

export const fxRevaluationSchema = z.object({
  /** Null for a preview that has not been posted. */
  id: uuidSchema.nullable(),
  period: fxPeriodSchema.nullable(),
  asOf: dateOnlySchema,
  /** Day 1 of the next period, when the revaluation reverses. */
  reversesOn: dateOnlySchema,
  baseCurrency: currencyCodeSchema,
  posted: z.boolean(),
  postedAt: timestampSchema.nullable(),
  postedByName: z.string().nullable(),
  invoiceCount: z.number().int(),
  carryingBaseMinor: minorSchema,
  revaluedBaseMinor: minorSchema,
  unrealisedBaseMinor: minorSchema,
  byCurrency: z.array(fxRevaluationCurrencySchema),
  lines: z.array(fxRevaluationLineSchema),
});
export type FxRevaluationDto = z.infer<typeof fxRevaluationSchema>;

/** GET /api/v1/fx/revaluation?period=YYYY-MM → the posted snapshot, or a live preview (`posted: false`). */
export const fxRevaluationQuerySchema = z.object({ period: fxPeriodSchema });
export type FxRevaluationQuery = z.infer<typeof fxRevaluationQuerySchema>;

/** POST /api/v1/fx/revaluations (perm `invoices:write` + `financials:view`) → 201. Already posted → CONFLICT. */
export const postFxRevaluationInputSchema = z.object({ period: fxPeriodSchema }).strict();
export type PostFxRevaluationInput = z.input<typeof postFxRevaluationInputSchema>;

/** GET /api/v1/fx/reports/unrealised?asOf (perms `invoices:read` + `financials:view`) → a live revaluation at any date. */
export const fxUnrealisedQuerySchema = z.object({ asOf: dateOnlySchema.optional() });
export type FxUnrealisedQuery = z.infer<typeof fxUnrealisedQuerySchema>;

// ---------------------------------------------------------------------------
// Per-currency breakdown (US-17.6: "a toggle shows original currencies")
// ---------------------------------------------------------------------------

export const fxCurrencyBreakdownRowSchema = z.object({
  currency: currencyCodeSchema,
  /** True for every currency other than the base: its base figures are converted. */
  converted: z.boolean(),
  billedMinor: minorSchema,
  billedBaseMinor: minorSchema,
  collectedMinor: minorSchema,
  collectedBaseMinor: minorSchema,
  outstandingMinor: minorSchema,
  outstandingBaseMinor: minorSchema,
  documentCount: z.number().int(),
  receiptCount: z.number().int(),
});
export type FxCurrencyBreakdownRowDto = z.infer<typeof fxCurrencyBreakdownRowSchema>;

/** GET /api/v1/fx/reports/currencies?from&to (perms `invoices:read` + `financials:view`). */
export const fxCurrencyBreakdownQuerySchema = z
  .object({ from: dateOnlySchema, to: dateOnlySchema, clientId: uuidSchema.optional() })
  .refine((q) => q.from <= q.to, { message: "`from` must be on or before `to`.", path: ["from"] });
export type FxCurrencyBreakdownQuery = z.infer<typeof fxCurrencyBreakdownQuerySchema>;

export const fxCurrencyBreakdownSchema = z.object({
  baseCurrency: currencyCodeSchema,
  from: dateOnlySchema,
  to: dateOnlySchema,
  currencies: z.array(fxCurrencyBreakdownRowSchema),
  /** Sums of the base columns. Label them "converted" whenever `mixed` is true. */
  totals: z.object({
    billedBaseMinor: minorSchema,
    collectedBaseMinor: minorSchema,
    outstandingBaseMinor: minorSchema,
  }),
  mixed: z.boolean(),
  label: z.string(),
});
export type FxCurrencyBreakdownDto = z.infer<typeof fxCurrencyBreakdownSchema>;

// ---------------------------------------------------------------------------
// Export realisation tracker (integrations-reference §1.8)
// ---------------------------------------------------------------------------

export const FX_EXPORT_STATUSES = ["open", "realised", "all"] as const;

/** GET /api/v1/fx/exports/realisation?status (perms `invoices:read` + `financials:view`). */
export const fxExportRealisationQuerySchema = z.object({ status: z.enum(FX_EXPORT_STATUSES).optional() });
export type FxExportRealisationQuery = z.infer<typeof fxExportRealisationQuerySchema>;

export const fxExportRealisationRowSchema = z.object({
  invoiceId: uuidSchema,
  clientId: uuidSchema,
  invoiceNumber: z.string().nullable(),
  invoiceDate: dateOnlySchema,
  currency: currencyCodeSchema,
  exportUnderLut: z.boolean(),
  totalMinor: minorSchema,
  totalBaseMinor: minorSchema,
  realisedMinor: minorSchema,
  outstandingMinor: minorSchema,
  realised: z.boolean(),
  /** Invoice date + one year: the LUT realisation deadline. */
  deadline: dateOnlySchema,
  daysSinceInvoice: z.number().int(),
  /** Unrealised and FX_EXPORT_ALERT_DAYS or more old. */
  atRisk: z.boolean(),
  firaNumbers: z.array(z.string()),
  ebrcNumbers: z.array(z.string()),
  purposeCodes: z.array(z.string()),
});
export type FxExportRealisationRowDto = z.infer<typeof fxExportRealisationRowSchema>;

// ---------------------------------------------------------------------------
// Base currency lock (PRD §5.1, principles §14.6)
// ---------------------------------------------------------------------------

export const fxBaseCurrencyStatusSchema = z.object({
  baseCurrency: currencyCodeSchema,
  enabledCurrencies: z.array(currencyCodeSchema),
  locked: z.boolean(),
  lockedReason: z.string().nullable(),
  /** Only the workspace owner may override the lock. */
  canOverride: z.boolean(),
  /** What an override does NOT do — shown before the owner confirms. */
  warning: z.string().nullable(),
});
export type FxBaseCurrencyStatusDto = z.infer<typeof fxBaseCurrencyStatusSchema>;

/**
 * POST /api/v1/fx/base-currency (perm `org:manage`, owner only) → FxBaseCurrencyStatus.
 * Overrides the lock after issued documents; `confirm: true` acknowledges the warning.
 */
export const overrideBaseCurrencyInputSchema = z
  .object({
    baseCurrency: currencyCodeSchema,
    confirm: z.literal(true, { error: "Confirm that you have read the warning." }),
    reason: z.string().trim().min(3).max(500),
  })
  .strict();
export type OverrideBaseCurrencyInput = z.input<typeof overrideBaseCurrencyInputSchema>;
